import React from 'react';
import SevereColdSharp from '@mui/icons-material/SevereColdSharp';

import { Badge } from 'src/component/ds/ui/badge';
import { Card, CardContent, CardHeader } from 'src/component/ds/ui/card';
import { Skeleton } from 'src/component/ds/ui/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'src/component/ds/ui/table';
import { cn } from 'src/component/ds/utils';
import { useTranslation } from 'src/i18n';
import { FORECAST_DAY_COUNT, type AmbientTemperatureDay } from './AmbientTemperature/ambientTemperature';

export interface AmbientTemperatureProps {
  days: AmbientTemperatureDay[];
  freezingDayCount: number;
  freezingLabel: string;
  loading?: boolean;
  className?: string;
}

const CELL = 'ds-px-2 ds-py-[7.5px]';
const DATE_CELL = 'ds-w-[106px]';

/* A badge marks any temperature at or below freezing, high or low alike. */
function TemperatureCell ({ value, freezing, bold }: { value: string; freezing: boolean; bold: boolean }) {
  if (!freezing) return <TableCell className={cn(CELL, bold ? 'ds-font-bold' : 'ds-font-normal')}>{value}</TableCell>;

  return (
    <TableCell className='ds-px-2 ds-py-0.5'>
      <Badge variant='secondary' data-testid='ambient-temperature-freezing'>
        {bold ? <span className='ds-font-bold'>{value}</span> : value}
      </Badge>
    </TableCell>
  );
}

export default function AmbientTemperature ({
  days,
  freezingDayCount,
  freezingLabel,
  loading = false,
  className
}: AmbientTemperatureProps) {
  const { t, tPlural } = useTranslation();

  const skeletonRows = Array.from({ length: FORECAST_DAY_COUNT }, (_, index) => index);

  return (
    <Card
      className={cn('ds-w-full ds-shadow-ds-sm ds-map-overlay:ds-w-[278px]', className)}
      data-testid='ambient-temperature'
    >
      <CardHeader className='ds-p-4'>
        <h3 className='ds-text-ds-base ds-font-semibold ds-text-foreground'>
          {t('dashboard.ambientTemperature')}
        </h3>
      </CardHeader>

      {freezingDayCount > 0
        ? (
          <div className='ds-px-4 ds-py-1'>
            <Badge variant='secondary' data-testid='ambient-temperature-freezing-summary'>
              <SevereColdSharp fontSize='inherit' aria-hidden='true' />
              {tPlural('dashboard.daysBelowFreezing', freezingDayCount, { temp: freezingLabel })}
            </Badge>
          </div>
        )
        : null}

      <CardContent className='ds-p-4 ds-pt-4'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className={DATE_CELL}>{t('dashboard.ambientTemperatureDate')}</TableHead>
              <TableHead>{t('dashboard.ambientTemperatureHigh')}</TableHead>
              <TableHead>{t('dashboard.ambientTemperatureLow')}</TableHead>
            </TableRow>
          </TableHeader>

          <TableBody>
            {loading
              ? skeletonRows.map((row) => (
                <TableRow key={row}>
                  <TableCell className={cn(CELL, DATE_CELL)}>
                    <Skeleton className='ds-h-4 ds-w-[70px]' data-testid='ambient-temperature-skeleton' />
                  </TableCell>
                  <TableCell className={CELL}><Skeleton className='ds-h-4 ds-w-6' /></TableCell>
                  <TableCell className={CELL}><Skeleton className='ds-h-4 ds-w-6' /></TableCell>
                </TableRow>
              ))
              : days.map((day, index) => {
                const isToday = index === 0;
                const weight = isToday ? 'ds-font-bold' : 'ds-font-normal';

                return (
                  <TableRow key={day.key} data-testid='ambient-temperature-row'>
                    <TableCell className={cn(CELL, DATE_CELL, weight)}>{day.label}</TableCell>
                    <TemperatureCell value={day.high} freezing={day.isHighFreezing} bold={isToday} />
                    <TemperatureCell value={day.low} freezing={day.isLowFreezing} bold={isToday} />
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
